import { useEffect, useCallback } from 'react'
import { useAuthStore } from '@/store/authStore'
import { authService, dbService } from '@/services/firebase'
import { disablePush } from '@/services/firebase/push'
import type { User } from '@/types'
import { setLoginNotice } from '@/utils/authErrors'

let announcementsResumedThisSession = false
let keteVisibilityMigratedThisSession = false

export function useAuth() {
  const {
    user,
    isLoading,
    isAuthenticated,
    setUser,
    setLoading,
    logout: storeLogout
  } = useAuthStore()

  // Listen to Firebase auth state changes
  useEffect(() => {
    const unsubscribe = authService.onAuthStateChanged(async (firebaseUser) => {
      if (firebaseUser) {
        let userData = await dbService.getUser(firebaseUser.uid)

        // Race with authService.signUp: createUserWithEmailAndPassword
        // fires onAuthStateChanged immediately, and signUp's own createUser
        // is still in flight. Poll briefly before falling back to a
        // self-write so we don't race into a permission-denied conflict.
        if (!userData) {
          for (let i = 0; i < 5 && !userData; i++) {
            await new Promise((resolve) => setTimeout(resolve, 250))
            userData = await dbService.getUser(firebaseUser.uid)
          }
        }

        // Fallback recovery for orphaned Auth users with no Firestore doc.
        // Lands as 'pending' to match the self-signup approval gate; if a
        // sibling write got there first, getUser picks that up instead.
        if (!userData) {
          try {
            userData = await dbService.createUser(firebaseUser.uid, {
              email: firebaseUser.email || '',
              displayName: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Member',
              role: 'member',
              status: 'pending',
              preferences: { emailNotifications: true },
            })
          } catch {
            // Most likely the parallel signUp write won — refetch.
            userData = await dbService.getUser(firebaseUser.uid)
          }
        }

        if (userData?.status === 'deactivated') {
          // Admin has deactivated this account — sign them out immediately
          // and bail before any reads/writes that would be rule-blocked.
          await authService.signOut().catch(() => {})
          setUser(null)
          setLoginNotice('This account has been deactivated. Please contact a Kiwi Church admin if this is unexpected.')
          return
        }

        if (userData) {
          setUser(userData)
          dbService.ensureCommunityGathering().catch(() => {})
          dbService.ensureCommunityMembership(firebaseUser.uid).catch(() => {})
          // One-time migration for pre-existing users that have no public
          // profile mirror yet. Cheap idempotent write — re-runs are fine.
          dbService.writeUserProfileMirror(userData).catch(() => {})
          // Resume any announcement broadcasts that were interrupted (admin only).
          if (userData.role === 'admin' && !announcementsResumedThisSession) {
            announcementsResumedThisSession = true
            dbService.resumePendingAnnouncements()
              .catch(err => console.error('[auth] resume announcements failed', err))
          }
          // Backfill kete `visibility` field on legacy posts (admin/host only).
          // Idempotent — re-runs find nothing to do.
          if (
            (userData.role === 'admin' || userData.role === 'host') &&
            !keteVisibilityMigratedThisSession
          ) {
            keteVisibilityMigratedThisSession = true
            dbService.migrateKeteVisibility()
              .catch(err => console.error('[auth] kete visibility migration failed', err))
          }
        } else {
          // Genuinely stuck — at least surface it to the user.
          // Sign out so they aren't stuck half-signed-in, and say why.
          console.error('[Auth] Could not load or create user doc for', firebaseUser.uid)
          await authService.signOut().catch(() => {})
          setUser(null)
          setLoginNotice("You're signed in, but we couldn't load your profile. Check your connection and try again — if it keeps happening, contact a Kiwi Church admin.")
        }
      } else {
        setUser(null)
      }
    })

    return unsubscribe
  }, [setUser])

  // Sign in
  const signIn = useCallback(async (identifier: string, password: string) => {
    setLoading(true)
    try {
      await authService.signIn(identifier, password)
      // Auth state listener will update the store
    } catch (error) {
      setLoading(false)
      throw error
    }
  }, [setLoading])

  // Sign up
  const signUp = useCallback(async (
    email: string,
    password: string,
    displayName?: string,
    username?: string
  ) => {
    setLoading(true)
    try {
      await authService.signUp(email, password, { displayName, username })
      // Auth state listener will update the store
    } catch (error) {
      setLoading(false)
      throw error
    }
  }, [setLoading])

  // Sign out
  const signOut = useCallback(async () => {
    try {
      // Stop pushes to this device first (needs the session for the Firestore delete),
      // so a shared phone/iPad doesn't keep getting the previous person's notifications.
      if (user?.id) await disablePush(user.id).catch(() => {})
      await authService.signOut()
      storeLogout()
    } catch (error) {
      console.error('Sign out error:', error)
      throw error
    }
  }, [storeLogout, user?.id])

  // Password reset
  const sendPasswordReset = useCallback(async (email: string) => {
    await authService.sendPasswordResetEmail(email)
  }, [])

  // Update profile
  const updateProfile = useCallback(async (updates: Partial<Pick<User, 'displayName' | 'photoURL' | 'phone' | 'bio' | 'privacy' | 'interests' | 'gallery' | 'dependants' | 'mutedNotificationCategories'>>) => {
    const updatedUser = await authService.updateProfile(updates)
    if (updatedUser) {
      setUser(updatedUser)
    }
    return updatedUser
  }, [setUser])

  // Update password. Also clears the seeded-default-password banner flag
  // so the HomePage banner disappears once they've set their own.
  const updatePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    const result = await authService.updatePassword(currentPassword, newPassword)
    if (user?.mustChangePassword) {
      try {
        await dbService.updateUser(user.id, { mustChangePassword: false })
        setUser({ ...user, mustChangePassword: false })
      } catch (err) {
        console.error('[useAuth] failed clearing mustChangePassword flag:', err)
      }
    }
    return result
  }, [user, setUser])

  // Role checks
  const isAdmin = user?.role === 'admin'
  const isHost = user?.role === 'host'
  const isAdminOrHost = isAdmin || isHost
  const canPostKete = isAdminOrHost || user?.canPostKete === true

  return {
    // State
    user,
    isLoading,
    isAuthenticated,
    isAdmin,
    isHost,
    isAdminOrHost,
    canPostKete,

    // Actions
    signIn,
    signUp,
    signOut,
    sendPasswordReset,
    updateProfile,
    updatePassword
  }
}
